import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Pagination
} from 'react-bootstrap';
import {
  FaChartBar,
  FaCopy,
  FaEdit,
  FaEllipsisV,
  FaQrcode,
  FaTrash
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import './style.css';
import { deleteProperty, getProperties } from 'Api/PropertyApi';
import { toast } from 'react-hot-toast';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { connectMerchant, createCheckoutSession } from 'Api/StripeApi';

type Property = {
  id: string | number;
  name: string;
  location: string;
  qrCode: string;
  shortUrl: string;
  propertyUrl: string;
  createdAt: string;
  visitCount: number;
};

function AgentPropertyView() {
  const user = useSelector((state: RootState) => state.auth.user);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const fetchingRoomDataRef = useRef(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [propertiesPerPage] = useState(3);
  const role = useSelector((state: RootState) => state.auth.user?.role);

  const handleCreateProperty = () => {
    navigate('/dashboard/properties/createProperty');
  };

  const CustomToggle = React.forwardRef<HTMLButtonElement, any>(
    ({ onClick }, ref) => (
      <button
        ref={ref}
        onClick={e => {
          e.preventDefault();
          onClick(e);
        }}
        className="border-0 bg-transparent p-0"
      >
        <FaEllipsisV style={{ fontSize: '1.4rem', color: '#333' }} />
      </button>
    )
  );

  const filteredProperties = Array.isArray(properties)
    ? properties.filter(property => {
        const isNameMatch = property.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const isLocationMatch = property.location
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        return isNameMatch || isLocationMatch;
      })
    : [];

  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(
    indexOfFirstProperty,
    indexOfLastProperty
  );

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success(' Link copied successfully');
  };
  const handleDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'qr-code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = (property: Property) => {
    setPropertyToDelete(property);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (propertyToDelete) {
      deletePropertyFromAPI(propertyToDelete.id);
      setShowDeleteModal(false);
    }
  };

  const deletePropertyFromAPI = async (propertyId: string | number) => {
    try {
      const response = await deleteProperty(propertyId);

      if (response.status === 200) {
        setProperties(
          properties.filter(property => property.id !== propertyId)
        );
        FetchPropertiesData();
        toast.success('Property deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Error deleting property.');
    }
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
  };

  const FetchPropertiesData = async () => {
    if (fetchingRoomDataRef.current) return;
    fetchingRoomDataRef.current = true;
    setLoading(true);
    try {
      if (!user?.id) {
        console.error('User ID is missing.');
        return;
      }

      const response = await getProperties(user.id);

      if (response.status === 200) {
        setProperties(response.data);
      } else {
        console.error('Error fetching properties. Status:', response.status);
        toast.error('Failed to load properties.');
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error fetching properties.');
    } finally {
      setLoading(false);
      fetchingRoomDataRef.current = false;
    }
  };

  useEffect(() => {
    if (user?.id) {
      FetchPropertiesData();
    }
  }, [user]);

  const handleEditClick = (property: Property) => {
    navigate('/dashboard/properties/createProperty', {
      state: { propertyId: property.id, property }
    });
  };

  // const stripePromise = loadStripe(
  //   'pk_test_51OTiBlSJ3HBNLlr2b9nmPmnUzw7chUBE6J4HuU6ZSmEqU8EWA1ntKV0xw2jjPS4gSIv1QWJlGboSvDFfSmIh2m0u0095dH4qHM'
  // );

  // const handleAddMoney = async () => {
  //   const stripe = await stripePromise;
  //   const amount = 1000;

  //   if (!stripe) {
  //     console.error('Stripe failed to load');
  //     return;
  //   }

  //   try {
  //     const response = await createCheckoutSession(amount);
  //     const result = await stripe.redirectToCheckout({
  //       sessionId: response.id
  //     });

  //     if (result?.error) {
  //       console.error(result.error.message);
  //     }
  //   } catch (error) {
  //     console.error('Error redirecting to Stripe:', error);
  //   }
  // };

  // const handleConnectMerchant = async () => {
  //   try {
  //     const { url } = await connectMerchant();
  //     window.location.href = url;
  //   } catch (error) {
  //     alert('Failed to connect merchant.');
  //   }
  // };

  return (
    <>
      <div className="container-small">
        <Row className="g-5 mb-5 mb-lg-4 ">
          <Col xs={12} xl={12}>
            <div className="">
              <div className="d-flex align-items-center justify-content-between">
                <div className="page-title-heading">
                  <div>
                    <h2 className="mb-3 lh-sm">Properties</h2>
                  </div>
                </div>

                {role === 'user' && (
                  <div className="mt-3 mt-md-0 ms-3 ms-md-0">
                    <Button
                      className="btn btn-primary px-5 w-md-auto ms-4"
                      style={{ marginRight: '18px' }}
                      onClick={handleCreateProperty}
                    >
                      Create property
                    </Button>
                    {/* 
                    <Button
                      className="btn btn-primary px-5 w-md-auto ms-4"
                      style={{ marginRight: '18px' }}
                      // onClick={handleAddMoney}
                      onClick={handleConnectMerchant}
                    >
                      Add Money
                    </Button> */}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
        {filteredProperties.length > 0 && (
          <Row className="g-5 mb-5 mb-lg-4">
            <Col xs={12} xl={12}>
              <div className="d-flex align-items-center">
                <Col md={4}>
                  <Form.Group className="form-icon-container d-sm-none d-xl-block">
                    <Form.Control
                      type="text"
                      placeholder="Search properties by name"
                      className="form-icon-input"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </div>
            </Col>
          </Row>
        )}

        <div className="border-y"></div>

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center "
            style={{ height: '70vh', width: '60vw' }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Row className="g-5 mb-5 mb-lg-8 mt-3">
            {currentProperties.length > 0 ? (
              currentProperties.map(property => (
                <Col xs={12} sm={12} md={12} lg={12} key={property.shortUrl}>
                  <Card className="p-4 shadow-sm rounded mb-4">
                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
                      {/* <div className="d-none d-sm-block">
                        <img
                          src={property.qrCode}
                          alt="QR Code"
                          className="img-fluid"
                          style={{ maxWidth: '100px', height: 'auto' }}
                        />
                      </div> */}
                      <div className="d-none d-sm-block">
                        <img
                          src={property.qrCode}
                          alt="QR Code"
                          className="img-fluid"
                          style={{ maxWidth: '100px', height: 'auto' }}
                        />
                      </div>
                      <div className="flex-grow-1">
                        <h5
                          className="mb-1 text-break text-wrap"
                          style={{ maxWidth: '800px', wordBreak: 'break-word' }}
                        >
                          {property.name} – {property.location}
                        </h5>

                        <Link
                          to={`/property/${property.shortUrl}`}
                          className="text-primary d-none d-sm-block text-break"
                        >
                          {property.propertyUrl}
                        </Link>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        {/* QR Code, visible on all screen sizes except small screens */}

                        {/* Icons, visible inline */}
                        {role !== 'admin' && (
                          <div className="d-none d-sm-flex flex-row flex-wrap align-items-center gap-2  mt-sm-0">
                            <Button
                              variant="light"
                              className="btn-sm"
                              onClick={() => handleCopy(property.propertyUrl)}
                              title="Copy URL"
                            >
                              <FaCopy style={{ fontSize: '1.2rem' }} />
                            </Button>

                            <Button
                              variant="light"
                              onClick={() => handleEditClick(property)}
                              title="Edit Property"
                            >
                              <FaEdit style={{ fontSize: '1.2rem' }} />
                            </Button>

                            <Dropdown>
                              <Dropdown.Toggle
                                as={CustomToggle}
                                title="More options"
                              />
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  className="d-flex align-items-center"
                                  onClick={() => handleDeleteClick(property)}
                                  title="Delete"
                                >
                                  <FaTrash className="me-2" />
                                  Delete
                                </Dropdown.Item>
                                <Dropdown.Item
                                  className="d-flex align-items-center"
                                  onClick={() =>
                                    handleDownload(property.qrCode)
                                  }
                                  title="Download QR Code"
                                >
                                  <FaQrcode className="me-2" />
                                  Download QR Code
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
                        {/* QR Code on mobile screens */}
                        <div className="d-block d-sm-none d-flex align-items-center gap-2" >
                          <img
                            src={property.qrCode}
                            alt="QR Code"
                            className="img-fluid"
                            style={{ maxWidth: '80px', height: 'auto' }}
                          />

                          {role !== 'admin' && (
                            <div className="d-flex flex-row flex-wrap align-items-center gap-2" >
                              <Button
                                variant="light"
                                className="btn-sm"
                                onClick={() => handleCopy(property.propertyUrl)}
                                title="Copy URL"
                              >
                                <FaCopy style={{ fontSize: '1.2rem' }} />
                              </Button>

                              <Button
                                variant="light"
                                onClick={() => handleEditClick(property)}
                                title="Edit Property"
                              >
                                <FaEdit style={{ fontSize: '1.2rem' }} />
                              </Button>

                              <Dropdown>
                                <Dropdown.Toggle
                                  as={CustomToggle}
                                  title="More options"
                                />
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    className="d-flex align-items-center"
                                    onClick={() => handleDeleteClick(property)}
                                    title="Delete"
                                  >
                                    <FaTrash className="me-2" />
                                    Delete
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className="d-flex align-items-center"
                                    onClick={() =>
                                      handleDownload(property.qrCode)
                                    }
                                    title="Download QR Code"
                                  >
                                    <FaQrcode className="me-2" />
                                    Download QR Code
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Bottom: Stats */}
                    <div className="d-flex flex-row flex-wrap align-items-center text-muted small mt-3 gap-2">
                      <div className="d-flex align-items-center">
                        <FaChartBar className="me-2 ms-3" />
                        <span className="me-3 ">
                          Lead {property.visitCount}
                        </span>
                      </div>
                      <span>📅 {property.createdAt}</span>
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              <p className="text-center fw-bold" style={{ color: 'red' }}>
                No properties found.
              </p>
            )}
          </Row>
        )}
        {filteredProperties.length > propertiesPerPage && (
          <Row className=" justify-content-end">
            <Col md={6}>
              <Pagination className="d-flex justify-content-end">
                <Pagination.Prev
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {Array.from(
                  {
                    length: Math.ceil(
                      filteredProperties.length / propertiesPerPage
                    )
                  },
                  (_, i) => (
                    <Pagination.Item
                      key={i + 1}
                      active={i + 1 === currentPage}
                      onClick={() => paginate(i + 1)}
                    >
                      {i + 1}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  onClick={() => paginate(currentPage + 1)}
                  disabled={
                    currentPage ===
                    Math.ceil(filteredProperties.length / propertiesPerPage)
                  }
                />
              </Pagination>
            </Col>
          </Row>
        )}
      </div>

      {/* Delete modal */}
      <Modal show={showDeleteModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the property{' '}
          <span className="fw-bold me-2">{propertyToDelete?.name}?</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AgentPropertyView;
